import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import {
  PRODUCT_IDS,
  ensureStoreInitialized,
  extractIapReceiptFromTransaction,
  getPendingReceiptKey,
  queuePendingReceipt,
  removePendingReceipt,
  replayPendingReceipts,
} from '@/hooks/useInAppPurchase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/**
 * Top-level component that initializes the CdvPurchase store at app launch
 * so that pending, interrupted, and promoted purchases are handled immediately
 * — even before the user reaches a paywall screen.
 *
 * Apple requires SKPaymentTransactionObserver to be registered at launch and
 * transactions to be processed seamlessly. The native Cordova plugin registers
 * the observer via onload="true", but the JS layer must also be ready to
 * handle the `approved` callback.
 */
const IAPTransactionObserver = ({ userId }) => {
  const initializedRef = useRef(false);
  // Closure-safe userId reference \u2014 same fix as useInAppPurchase.
  const userIdRef = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // On every userId change, replay any receipts queued from earlier sessions.
  useEffect(() => {
    if (!userId) return;
    replayPendingReceipts(userId).then((r) => {
      if (r.attempted > 0) {
        console.log(`[IAP-Observer] Replayed pending receipts: ${r.succeeded}/${r.attempted}`);
      }
    });
  }, [userId]);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') return;
    if (initializedRef.current) return;
    // Note: we still allow init without userId so we can capture early
    // approved transactions and queue them for later verification.

    const initStore = async () => {
      try {
        const { store, ProductType, Platform, LogLevel } = window.CdvPurchase || {};
        if (!store) return;

        // If useInAppPurchase already initialized (or is initializing) the
        // store, skip — a second initialize() never resolves.
        if (store._iapObserverReady || store._iapInitPromise) return;

        initializedRef.current = true;
        store.verbosity = LogLevel?.WARNING || 1;

        const storePlatform = platform === 'ios'
          ? Platform.APPLE_APPSTORE
          : Platform.GOOGLE_PLAY;

        if (userId) {
          store.applicationUsername = userId;
        }

        // Handle approved transactions \u2014 verify receipt with backend.
        // Same closure-safety contract as useInAppPurchase: read userId via ref.
        store.when().approved(async (transaction) => {
          console.log('[IAP-Observer] Transaction approved:', transaction.transactionId);
          if (store._iapPurchaseInProgress) {
            console.log('[IAP-Observer] Skipping \u2014 useInAppPurchase is handling this');
            return;
          }
          const receiptPayload = extractIapReceiptFromTransaction(transaction, platform);

          // Persist immediately so we never lose the receipt — even if
          // receipt_data is empty: the backend will reconcile via txId
          // / Apple Server Notifications.
          queuePendingReceipt(receiptPayload);

          const currentUserId = userIdRef.current;
          if (!currentUserId) {
            console.warn('[IAP-Observer] No userId yet \u2014 receipt queued for replay');
            return;
          }

          try {
            const response = await axios.post(`${API}/subscription/verify-iap`, {
              user_id: currentUserId,
              platform: receiptPayload.platform,
              receipt_data: receiptPayload.receipt_data,
              product_id: receiptPayload.product_id,
              transaction_id: receiptPayload.transaction_id,
              original_transaction_id: receiptPayload.original_transaction_id,
              price: receiptPayload.price,
              currency: receiptPayload.currency,
            });

            if (response?.status !== 200 || response?.data?.status !== 'ok') {
              throw new Error(`verify-iap returned non-ok status: ${response?.data?.status || 'unknown'}`);
            }

            removePendingReceipt(getPendingReceiptKey(receiptPayload));
            await transaction.finish();
            console.log('[IAP-Observer] Transaction verified and finished');
          } catch (err) {
            console.error('[IAP-Observer] Receipt verification failed (will retry):', err);
          }
        });

        store.when().finished((transaction) => {
          console.log('[IAP-Observer] Transaction finished:', transaction.transactionId);
        });

        store.error((err) => {
          if (err.code !== 'CdvPurchase.PAYMENT_CANCELLED') {
            console.error('[IAP-Observer] Store error:', err);
          }
        });

        // Shared with useInAppPurchase: whichever mounts first performs the
        // one and only initialize(), the other awaits the same promise. Product
        // registration happens inside the helper so it also runs exactly once.
        await ensureStoreInitialized(store, storePlatform, () => {
          store.register([
            { id: PRODUCT_IDS.LIFETIME, type: ProductType.NON_CONSUMABLE, platform: storePlatform },
            // Retired subscriptions stay registered so renewals and restores
            // from existing subscribers are still delivered and verified.
            { id: PRODUCT_IDS.MONTHLY, type: ProductType.PAID_SUBSCRIPTION, platform: storePlatform },
            { id: PRODUCT_IDS.ANNUAL, type: ProductType.PAID_SUBSCRIPTION, platform: storePlatform },
          ]);
        });
        await store.update();
        console.log('[IAP-Observer] Store initialized at app launch');
      } catch (err) {
        console.error('[IAP-Observer] Init error:', err);
      }
    };

    if (window.CdvPurchase) {
      initStore();
    } else {
      document.addEventListener('deviceready', initStore, { once: true });
      setTimeout(() => {
        if (!initializedRef.current && window.CdvPurchase) initStore();
      }, 3000);
    }
  }, [userId]);

  return null;
};

export default IAPTransactionObserver;
